/// <amd-module name="Repos/Views/Settings/Repositories/Components/Repositories" />
import React = require("react");
import { GitRepository } from "Repos/Common/Contracts/Git";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { PanelCreateProps } from "Repos/Views/Import/Components/PanelCreate/PanelCreate.Props";
import { AllRepositories } from "Repos/Views/Settings/Repositories/Components/AllRepositories";
import { DeleteDialog } from "Repos/Views/Settings/Repositories/Components/DeleteDialog";
import { RenameDialog } from "Repos/Views/Settings/Repositories/Components/RenameDialog";
import { RepositoriesMasterDetails } from "Repos/Views/Settings/Repositories/Components/RepositoriesMasterDetails";
import { IRepository } from "Repos/Views/Settings/Repositories/Contracts/Contracts";
import { IRepositoriesViewService } from "Repos/Views/Settings/Repositories/Services/RepositoriesViewService";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { IPerformanceScenarioPendingWork, PerformanceScenario, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IStartRightExtensionService } from "VSS/Platform/StartRightExtension";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { CrossRepoBranchPolicy } from "./CrossRepoBranchPolicy";

class RepositoriesComponent extends VssComponent {
    private hideContentWhileLoad: IReadonlyObservableValue<boolean>;
    private pendingWork: IPerformanceScenarioPendingWork | undefined;
    private ready: IReadonlyObservableValue<boolean>;
    private svc: IRepositoriesViewService;

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.svc = context.pageContext.getService<IRepositoriesViewService>("RepositoriesViewService");
        this.hideContentWhileLoad = this.svc.getHideContentWhileLoad();
        this.ready = this.svc.getRepositories().ready;
    }

    public componentDidMount = () => {
        if (!this.ready.value) {
            this.ready.subscribe(this.onReady);
        } else {
            this.onReady(true);
        }
    };
    public componentWillUnmount = () => {
        super.componentWillUnmount();
        this.ready.unsubscribe(this.onReady);
    };

    public render = (): JSX.Element => {
        return (
            <PerformanceScenario area="Repos" scenarioName="repositories-settings" tti={true}>
                <Surface background={SurfaceBackground.neutral}>
                    <Page className="flex-grow">
                        <ReposMessages showPullRequestSuggestions={false} className="flex-noshrink" />

                        <PerformanceScenarioContext.Consumer>
                            {scenarioProps => {
                                this.pendingWork = this.pendingWork || scenarioProps.registerPendingWork("settings-repositories");

                                return (
                                    <Observer
                                        hideContentWhileLoad={this.hideContentWhileLoad}
                                        ready={this.ready}
                                        selected={this.svc.getSelected()}
                                        selectedRef={this.svc.getSelectedRefs()}
                                    >
                                        {(props: {
                                            hideContentWhileLoad: boolean;
                                            ready: boolean;
                                            selected: IRepository | undefined;
                                            selectedRef: string | undefined;
                                        }) => {
                                            if (props.hideContentWhileLoad) {
                                                return (
                                                    <div className="flex-grow justify-center flex-column padding-16">
                                                        <Spinner size={SpinnerSize.large} />
                                                    </div>
                                                );
                                            }

                                            if (props.selected) {
                                                return <RepositoriesMasterDetails renderCreatePanel={this.onCreate} />;
                                            }

                                            if (!props.selected && props.selectedRef) {
                                                return <CrossRepoBranchPolicy />;
                                            }

                                            return <AllRepositories renderCreatePanel={this.onCreate} />;
                                        }}
                                    </Observer>
                                );
                            }}
                        </PerformanceScenarioContext.Consumer>
                        <RenameDialog />
                        <DeleteDialog />
                    </Page>
                </Surface>
            </PerformanceScenario>
        );
    };

    private onCreate = async () => {
        const { pageContext } = this.context;

        const startRightExtensionService = pageContext.getService<IStartRightExtensionService>("IStartRightExtensionService");
        if (startRightExtensionService.navigateToCreateRepository(pageContext)) {
            return;
        }

        const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const project = this.svc.getProject();

            const onCreate = (val: Promise<{ repository: GitRepository | { webUrl: string } }>) => {
                this.svc.createRepository(val, onDismiss);
            };

            return (
                <WrappedComponent<PanelCreateProps>
                    dependencies={["ms.vss-code-web.import-content"]}
                    wrappedProps={{
                        onCreate,
                        onDismiss,
                        projectId: project.id!,
                        projectName: project.name!
                    }}
                    wrappedType="ms.vss-code-web.panel-create"
                />
            );
        });
    };

    private onReady = (ready: boolean) => {
        if (ready && this.pendingWork && !this.pendingWork.completed) {
            this.pendingWork.complete();
        }
    };
}

Components.add("repositories-component", RepositoriesComponent);
